import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { refreshSession, storeRefreshedTokens } from "../../auth/refreshSession";
import { getDeploymentConfig } from "../../deployment-config";
import { LS_KEYS, LOGIN_URL, POST_LOGIN_PATH_KEY } from "../../constants";
import { DESKTOP_SIGN_IN, isDesktop } from "../../../desktop/desktop";

// Same sign-in flow as the other kast-dev apps: we bounce to
// login.kast-dev.net, which posts Cognito tokens back to our origin in the
// URL hash. Sign-in is optional here; playing works anonymously and only
// cloud saves need an account.

export interface AuthTokens {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
}

export interface AuthState {
  userEmail: string | null;
  /** The display name from Cognito, null when the account has none. */
  userName: string | null;
  /** Cognito groups from the ID token, kept current as tokens refresh. */
  userGroups: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Groups that open CloudBoy's admin page: its own, and the shared kast-dev
// admins (as the other apps allow). Matches AdminUser in api/src/auth.rs;
// compared case-insensitively.
export const ADMIN_GROUPS = new Set(["pocket-play-admin", "admin"]);

export function isCloudBoyAdmin(groups: string[]): boolean {
  return groups.some((g) => ADMIN_GROUPS.has(g.toLowerCase()));
}

/** The account's display name from the id token's `name` claim, if set. */
export function extractName(idToken: string | null): string | null {
  const name = idToken ? decodeJwt(idToken)?.name : null;
  return typeof name === "string" && name.trim() ? name.trim() : null;
}

export function extractGroups(idToken: string | null): string[] {
  const groups = idToken ? decodeJwt(idToken)?.["cognito:groups"] : null;
  return Array.isArray(groups) ? groups.map(String) : [];
}

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function extractEmail(idToken: string): string {
  const d = decodeJwt(idToken);
  if (!d) return "";
  return String(d.email || d["cognito:username"] || d.sub || "");
}

function msUntilExpiry(token: string): number {
  const d = decodeJwt(token);
  if (!d) return 0;
  return Math.max(0, Number(d.exp || 0) * 1000 - Date.now());
}

async function refreshTokens(refreshToken: string): Promise<AuthTokens | null> {
  const { cognitoDomain, clientId } = getDeploymentConfig();
  const result = await refreshSession({ cognitoDomain, clientId }, refreshToken);
  return result.outcome === "ok" ? result.tokens : null;
}

// Tokens carry the account's groups as of when they were issued (ID tokens
// last 10 hours), so a newly granted group shows up only after a refresh.
// API clients call this on a 403 and retry once.
export async function refreshStoredTokens(): Promise<boolean> {
  const refresh = localStorage.getItem(LS_KEYS.refreshToken);
  const tokens = refresh ? await refreshTokens(refresh) : null;
  if (!tokens?.access_token && !tokens?.id_token) return false;
  storeRefreshedTokens(tokens);
  return true;
}

function storeTokens(state: AuthState, tokens: AuthTokens) {
  if (tokens.access_token) localStorage.setItem(LS_KEYS.accessToken, tokens.access_token);
  if (tokens.id_token) {
    localStorage.setItem(LS_KEYS.idToken, tokens.id_token);
    const email = extractEmail(tokens.id_token);
    state.userEmail = email;
    state.userName = extractName(tokens.id_token);
    state.userGroups = extractGroups(tokens.id_token);
    localStorage.setItem(LS_KEYS.username, email);
  }
  if (tokens.refresh_token) localStorage.setItem(LS_KEYS.refreshToken, tokens.refresh_token);
  state.isAuthenticated = true;
  state.isLoading = false;
}

function clearStoredTokens(state: AuthState) {
  Object.values(LS_KEYS).forEach((k) => localStorage.removeItem(k));
  state.userEmail = null;
  state.userName = null;
  state.userGroups = [];
  state.isAuthenticated = false;
  state.isLoading = false;
}

export const applyHashTokens = createAsyncThunk<AuthTokens, void, { rejectValue: string }>(
  "auth/applyHashTokens",
  async (_, { rejectWithValue }) => {
    const hash = window.location.hash;
    if (!hash) return rejectWithValue("No hash");
    const params = new URLSearchParams(hash.slice(1));
    const access_token = params.get("access_token");
    const id_token = params.get("id_token");
    const refresh_token = params.get("refresh_token");
    if (!access_token) return rejectWithValue("No access_token");
    window.history.replaceState({}, "", window.location.pathname);
    return {
      access_token,
      id_token: id_token || undefined,
      refresh_token: refresh_token || undefined,
    };
  },
);

export const validateToken = createAsyncThunk<string, void, { rejectValue: string }>(
  "auth/validateToken",
  async (_, { dispatch, rejectWithValue }) => {
    const email = localStorage.getItem(LS_KEYS.username);
    const access = localStorage.getItem(LS_KEYS.accessToken);
    if (!email || !access) return rejectWithValue("No auth");

    // Refresh on every page load, not just near expiry: tokens carry the
    // account's groups as of issue, so a new group (CloudBoy access, admin)
    // shows up on the next load instead of after the token's 10 hours.
    const refresh = localStorage.getItem(LS_KEYS.refreshToken);
    if (refresh) {
      const tokens = await refreshTokens(refresh);
      if (tokens) {
        dispatch(applyTokens(tokens));
        return email;
      }
    }
    if (msUntilExpiry(access) > TOKEN_REFRESH_BUFFER_MS) return email;
    return rejectWithValue("Token expired");
  },
);

const initialState: AuthState = {
  userEmail: localStorage.getItem(LS_KEYS.username) || null,
  userName: extractName(localStorage.getItem(LS_KEYS.idToken)),
  userGroups: extractGroups(localStorage.getItem(LS_KEYS.idToken)),
  isAuthenticated: !!(
    localStorage.getItem(LS_KEYS.username) &&
    (localStorage.getItem(LS_KEYS.idToken) || localStorage.getItem(LS_KEYS.accessToken))
  ),
  isLoading: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    applyTokens: (state, action: PayloadAction<AuthTokens>) => {
      storeTokens(state, action.payload);
    },
    logout: (state) => {
      clearStoredTokens(state);
      const cfg = getDeploymentConfig();
      if (cfg.cognitoDomain && cfg.clientId) {
        window.location.href = `${cfg.cognitoDomain}/logout?client_id=${cfg.clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
      }
    },
    redirectToLogin: (state) => {
      // The desktop app signs in through the system browser (DesktopSignIn)
      // instead of sending its own window away.
      if (isDesktop) {
        window.dispatchEvent(new Event(DESKTOP_SIGN_IN));
        return;
      }
      clearStoredTokens(state);
      try {
        sessionStorage.setItem(
          POST_LOGIN_PATH_KEY,
          window.location.pathname + window.location.search,
        );
      } catch {
        /* storage blocked; HashTokenHandler falls back to / */
      }
      const redirect = encodeURIComponent(window.location.origin);
      window.location.replace(`${LOGIN_URL}/signin?redirect=${redirect}`);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyHashTokens.fulfilled, (state, action) => {
        storeTokens(state, action.payload);
      })
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.userEmail = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(validateToken.rejected, (state) => {
        clearStoredTokens(state);
      });
  },
});

export const { applyTokens, logout, redirectToLogin } = authSlice.actions;
export default authSlice.reducer;
